import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiClock, FiArrowRight, FiFileText, FiCompass, FiActivity } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isModalOpen = isOpen || internalOpen;
  const isRTL = i18n.language === 'ar' || i18n.language === 'ckb';

  // Cmd+K / Ctrl+K Global Shortcut
  useEffect(() => {
    const handleCmdK = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setInternalOpen(true);
      }
    };
    window.addEventListener('keydown', handleCmdK);
    return () => window.removeEventListener('keydown', handleCmdK);
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) setRecentSearches(JSON.parse(savedSearches));
  }, []);

  // Body overflow & focus management
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 100);
      setSelectedIndex(0);
    } else {
      document.body.style.overflow = 'unset';
      setSearchValue('');
    }
  }, [isModalOpen]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };
    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalOpen]);

  const handleClose = () => {
    setInternalOpen(false);
    if (onClose) onClose();
  };

  const handleSearch = (query) => {
    const finalQuery = typeof query === 'string' ? query : searchValue;
    if (!finalQuery.trim()) return;

    const updatedSearches = [
      finalQuery,
      ...recentSearches.filter(item => item !== finalQuery)
    ].slice(0, 5);
    
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
    setRecentSearches(updatedSearches);
    
    handleClose();
    navigate(`/search?q=${encodeURIComponent(finalQuery)}`);
  };

  const removeRecent = (e, itemToRemove) => {
    e.stopPropagation();
    const updated = recentSearches.filter(item => item !== itemToRemove);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Mock Quick Actions (Categories)
  const quickActions = [
    { id: 'home', label: t('home', 'Home'), icon: <FiCompass className="w-5 h-5" />, path: '/' },
    { id: 'projects', label: t('projects', 'Projects'), icon: <FiActivity className="w-5 h-5" />, path: '/projects' },
    { id: 'about', label: t('about', 'About Us'), icon: <FiFileText className="w-5 h-5" />, path: '/about' },
  ];

  const filteredActions = quickActions.filter(action => 
    action.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const displayedRecent = searchValue ? [] : recentSearches;
  const totalItems = displayedRecent.length + filteredActions.length;

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % (totalItems || 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + (totalItems || 1)) % (totalItems || 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (searchValue.trim() && totalItems === 0) {
          handleSearch();
        } else if (totalItems > 0) {
          if (selectedIndex < displayedRecent.length) {
            handleSearch(displayedRecent[selectedIndex]);
          } else {
            const action = filteredActions[selectedIndex - displayedRecent.length];
            if (action) {
              handleClose();
              navigate(action.path);
            }
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, searchValue, selectedIndex, totalItems, displayedRecent, filteredActions]);

  return (
    <AnimatePresence>
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] sm:pt-[15vh] px-4" dir={isRTL ? "rtl" : "ltr"}>
          {/* Dark Blurred Backdrop */}
          {/* Using bg-opacity-50 instead of bg-black/50 for Tailwind v2 compatibility */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-filter backdrop-blur-sm z-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Modal Container */}
          <motion.div
            ref={modalRef}
            className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10 overflow-hidden flex flex-col z-10"
            initial={{ opacity: 0, scale: 0.97, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -10 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Search Input Bar */}
            <div className="flex items-center px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
              <FiSearch className="w-6 h-6 text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSelectedIndex(0);
                }}
                placeholder={t('searchPlaceholder', 'Search for anything...')}
                className="flex-1 bg-transparent border-none outline-none focus:ring-0 px-4 text-gray-900 dark:text-white placeholder-gray-400 text-lg sm:text-xl font-medium"
              />
              
              <div className="flex items-center gap-3 shrink-0">
                {searchValue && (
                  <button 
                    onClick={() => { setSearchValue(''); inputRef.current?.focus(); }}
                    className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                )}
                <div className="hidden sm:flex items-center justify-center px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-mono text-gray-500 dark:text-gray-400 font-medium tracking-wider">
                  ESC
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-col max-h-[60vh] overflow-y-auto scroll-smooth py-2">
              
              {/* Recent Searches */}
              {displayedRecent.length > 0 && (
                <div className="px-2 py-2">
                  <h3 className="px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">
                    {t('recentSearches', 'Recent Searches')}
                  </h3>
                  <div className="flex flex-wrap gap-2 px-4">
                    {displayedRecent.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSearch(item)}
                        className={`group flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-all duration-200 ${
                          selectedIndex === idx 
                            ? 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-900 dark:text-white shadow-sm' 
                            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <FiClock className="w-4 h-4 opacity-50" />
                        <span className="text-sm font-medium">{item}</span>
                        <button 
                          onClick={(e) => removeRecent(e, item)}
                          className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          <FiX className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions / Categories */}
              {filteredActions.length > 0 && (
                <div className="px-2 py-2">
                  <h3 className="px-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 mt-2">
                    {t('quickActions', 'Quick Actions')}
                  </h3>
                  <div className="flex flex-col gap-1">
                    {filteredActions.map((action, idx) => {
                      const actualIndex = displayedRecent.length + idx;
                      const isSelected = selectedIndex === actualIndex;
                      
                      return (
                        <div
                          key={action.id}
                          onClick={() => { handleClose(); navigate(action.path); }}
                          className={`flex items-center justify-between px-4 py-3 mx-2 rounded-xl cursor-pointer transition-all duration-200 ${
                            isSelected 
                              ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' 
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-2.5 rounded-lg transition-colors ${isSelected ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-gray-100 dark:bg-gray-800'}`}>
                              {action.icon}
                            </div>
                            <span className="font-semibold text-sm sm:text-base">{action.label}</span>
                          </div>
                          {isSelected && <FiArrowRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Results Fallback */}
              {searchValue && filteredActions.length === 0 && (
                <div className="px-4 py-16 text-center flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center mb-4 border border-gray-100 dark:border-gray-700">
                    <FiSearch className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-gray-900 dark:text-white font-semibold text-lg">
                    No results for "{searchValue}"
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Try searching for something else or check your spelling.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;